import React, { useCallback, useEffect, useRef, useState } from "react";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import WithdrawHistoryDetailModal from "./WithdrawHistoryDetailModal";
import { fetchWithdrawalHistory } from "../../../api/services/withdrawService";
import { mapWithdrawHistoryRow, withdrawalHistoryApiParse } from "../WithdrawPage/withdrawPageHelpers";
import { WITHDRAW_HISTORY_REFRESH, withdrawalDeclarationPath } from "./depositDeclarationModel";
import { DepositHistoryStatus } from "./DepositSenderConfirm";
import "./depositDeclaration.css";

/* Filters (restored later)
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import {
  matchesDepositCoin,
  matchesStatusNorm,
  matchesTypeNorm,
  rowWithinPastDays,
} from "./orderFilterHelpers";

const TYPE_OPTIONS = [
  { value: "crypto", label: "Crypto" },
  { value: "fiat", label: "Fiat" },
];
const TIME_OPTIONS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];
const COIN_OPTIONS_FALLBACK = [
  { value: "all", label: "All" },
  { value: "btc", label: "Bitcoin" },
  { value: "eth", label: "Ethereum" },
  { value: "matic", label: "Polygon" },
  { value: "ltc", label: "Litecoin" },
];
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
];
*/

const ORDER_DESKTOP_COL_COUNT = 7;
const PAGE_SIZE = 20;

function computeHasMore({ skip, batchLength, total, pageSize }) {
  if (batchLength === 0) return false;
  if (total != null) return skip + batchLength < total;
  return batchLength >= pageSize;
}

function orderHistoryClickIgnoreDetail(target) {
  if (!target || typeof target.closest !== "function") return false;
  return Boolean(target.closest("button, a[href]"));
}

function withdrawConfirmView(row) {
  return {
    id: row.id,
    action: "Provide Info",
    needsSenderConfirm: true,
  };
}

const WithdrawHistory = () => {
  const [rows, setRows] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const rowsRef = useRef([]);
  const hasMoreRef = useRef(true);
  const loadingInitialRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const desktopSentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingInitialRef.current = loadingInitial;
  }, [loadingInitial]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInitial(true);
      loadingInitialRef.current = true;
      setRows([]);
      rowsRef.current = [];
      setHasMore(true);
      hasMoreRef.current = true;
      try {
        const res = await fetchWithdrawalHistory(0, PAGE_SIZE);
        if (cancelled) return;
        const { rawRows, total } = withdrawalHistoryApiParse(res);
        const batch = (rawRows || []).map((raw, i) => mapWithdrawHistoryRow(raw, i));
        setRows(batch);
        rowsRef.current = batch;
        const more = computeHasMore({ skip: 0, batchLength: batch.length, total, pageSize: PAGE_SIZE });
        setHasMore(more);
        hasMoreRef.current = more;
      } catch {
        if (!cancelled) {
          setRows([]);
          rowsRef.current = [];
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
          loadingInitialRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    const onRefresh = () => setRefreshTick((n) => n + 1);
    window.addEventListener(WITHDRAW_HISTORY_REFRESH, onRefresh);
    return () => window.removeEventListener(WITHDRAW_HISTORY_REFRESH, onRefresh);
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMoreRef.current || loadingInitialRef.current || loadingMoreRef.current) return;
    const skip = rowsRef.current.length;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await fetchWithdrawalHistory(skip, PAGE_SIZE);
      const { rawRows, total } = withdrawalHistoryApiParse(res);
      const batch = (rawRows || []).map((raw, i) => mapWithdrawHistoryRow(raw, skip + i));
      if (!batch.length) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }
      setRows((prev) => {
        const next = [...prev, ...batch];
        rowsRef.current = next;
        return next;
      });
      const more = computeHasMore({ skip, batchLength: batch.length, total, pageSize: PAGE_SIZE });
      setHasMore(more);
      hasMoreRef.current = more;
    } catch {
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    const roots = [desktopSentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!roots.length) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible) return;
        if (!hasMoreRef.current || loadingInitialRef.current || loadingMoreRef.current) return;
        void loadMore();
      },
      { root: null, rootMargin: "160px", threshold: 0 },
    );

    roots.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [loadMore, rows.length, loadingInitial, hasMore, loadingMore]);

  const openDetail = useCallback((row) => {
    setDetailRow(row);
    setDetailOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailRow(null);
  }, []);

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const renderLoadingBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <span className="so-table__td--muted" role="status">
          Loading…
        </span>
      </td>
    </tr>
  );

  const StatusPill = ({ value }) => {
    const v = String(value || "");
    if (v === "Completed" || v === "Confirmed") return <span className="so-status so-status--completed">{value}</span>;
    if (
      v === "Pending" ||
      v === "Processing" ||
      v === "Queued" ||
      v === "Approved" ||
      /review/i.test(v)
    ) {
      return <span className="so-status so-status--pending">{value}</span>;
    }
    return <span className="so-badge--closed">{value}</span>;
  };

  const showEmpty = !loadingInitial && rows.length === 0;

  const subTabs = [
    { label: "Deposit", to: "/user_profile/orders/deposit_history" },
    { label: "Withdraw", to: "/user_profile/orders/withdraw_history" },
    { label: "Transfer", to: "/user_profile/orders/transfer" },
    // { label: "Referral", to: "/user_profile/orders/referral" },
  ];

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-withdraw-history-title">
            <h1 id="so-withdraw-history-title" className="so-page__title">
              Withdraw History
            </h1>

            <OrdersSubTabs tabs={subTabs} />

            {/* Filters — restore when product re-enables client filters
            {renderToolbar(false, "desk")}
            */}

            <div className="so-tableWrap">
              <table className="so-table">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Withdraw wallet</th>
                    <th scope="col">Coin</th>
                    <th scope="col" className="so-table__th--right">
                      Amount
                    </th>
                    <th scope="col">Destination</th>
                    <th scope="col">TxID</th>
                    <th scope="col" className="so-table__th--center">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loadingInitial
                    ? renderLoadingBody()
                    : showEmpty
                      ? renderEmptyBody()
                      : rows.map((row) => {
                          const r = row.orderTable;
                          return (
                            <tr
                              key={row.id}
                              className="so-orderHist-clickRow"
                              tabIndex={0}
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                if (orderHistoryClickIgnoreDetail(e.target)) return;
                                openDetail(row);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openDetail(row);
                                }
                              }}
                            >
                              <td className="so-num">{r.time}</td>
                            
                              <td className="so-num">{r.wallet}</td>
                              <td>
                                <div className="so-contractCell">
                                  <span className="so-contractCell__symbol">{r.coin}</span>
                                  <span className="so-contractCell__time">{r.chain}</span>
                                </div>
                              </td>
                              <td className="so-table__td--right so-num">{r.amount}</td>
                              <td>{r.destination}</td>
                              <td>
                                <div className="so-contractCell">
                                  <span className="so-contractCell__symbol so-num">{r.txid}</span>
                                  {r.txSub ? <span className="so-contractCell__time so-num">{r.txSub}</span> : null}
                                </div>
                              </td>
                              <td className="so-table__td--center">
                                {row.needsSenderConfirm ? (
                                  <DepositHistoryStatus
                                    row={withdrawConfirmView(row)}
                                    showConfirmButton={false}
                                    badgeClassName="so-depHist_badge is-pending"
                                    confirmTo={withdrawalDeclarationPath(row.id)}
                                  />
                                ) : (
                                  <StatusPill value={r.status} />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                  {!loadingInitial && !showEmpty && loadingMore ? (
                    <tr>
                      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
                        <span className="so-table__td--muted" role="status">
                          Loading more…
                        </span>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {!loadingInitial && rows.length > 0 ? (
              <div ref={desktopSentinelRef} className="so-transferInfiniteSentinel" aria-hidden="true" />
            ) : null}
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Withdraw History</h2>
          <OrdersSubTabs tabs={subTabs} />
          {/* <div className="so-mobile__toolbar">
            <SoMobileFilters ... />
          </div> */}
          {loadingInitial ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <span className="so-table__td--muted">Loading…</span>
            </div>
          ) : showEmpty ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            <>
              {rows.map((row) => {
                const r = row.orderTable;
                return (
                  <OrderMobileCardShell
                    key={`m-${row.id}`}
                    desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
                    popupTitle="Withdraw Details"
                    keepTypeInList
                    externalDetail
                    onOpenExternalDetail={() => openDetail(row)}
                  >
                    <div className="so-mobileCard__row">
                      <span>Time</span>
                      <span className="so-num">{r.time}</span>
                    </div>

                    <div className="so-mobileCard__row">
                      <span>Wallet</span>
                      <span className="so-num">{r.wallet}</span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Coin</span>
                      <span>
                        <div className="so-contractCell">
                          <span className="so-contractCell__symbol">{r.coin}</span>
                          <span className="so-contractCell__time">
                            {r.coinSub}
                            {r.chain && r.chain !== "—" ? ` · ${r.chain}` : ""}
                          </span>
                        </div>
                      </span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Amount</span>
                      <span className="so-num">{r.amount}</span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Destination</span>
                      <span>{r.destination}</span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>TxID</span>
                      <span className="so-mobileCard__stack">
                        <span className="so-num">{r.txid}</span>
                        {r.txSub ? <span className="so-table__td--muted so-num">{r.txSub}</span> : null}
                      </span>
                    </div>
                    <div className="so-mobileCard__row">
                      <span>Status</span>
                      <span>
                        {row.needsSenderConfirm ? (
                          <DepositHistoryStatus
                            row={withdrawConfirmView(row)}
                            showConfirmButton={false}
                            badgeClassName="so-depHist_badge is-pending"
                            confirmTo={withdrawalDeclarationPath(row.id)}
                          />
                        ) : (
                          <StatusPill value={r.status} />
                        )}
                      </span>
                    </div>
                  </OrderMobileCardShell>
                );
              })}
              {loadingMore ? (
                <div className="so-emptyState so-emptyState--mobile" role="status">
                  <span className="so-table__td--muted">Loading more…</span>
                </div>
              ) : null}
              {!loadingInitial && rows.length > 0 ? (
                <div ref={mobileSentinelRef} className="so-transferInfiniteSentinel" aria-hidden="true" />
              ) : null}
            </>
          )}
        </div>
      </div>

      <WithdrawHistoryDetailModal open={detailOpen} row={detailRow} onClose={closeDetail} />
    </div>
  );
};

export default WithdrawHistory;
